import type { ApplicationSettingResult } from '@/api/core/properties';
import { isApiError } from '@/api/http/errors';
import { UnsavedGuard } from '@/components/unsaved-guard';
import {
  Alert,
  AlertDescription,
  Button,
  ConfirmDialog,
  FormField,
  FormPage,
  Switch,
  Textarea,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ApplicationSettingRefreshError } from './property-queries';

const MAX_REASON_LENGTH = 240;
const refreshFailureMessage =
  '최신 설정을 확인하지 못했습니다. 현재값은 확인되지 않았으니 다시 시도하기 전에 지점 정보를 다시 조회하세요.';
export interface ApplicationSettingDraft {
  enabled: boolean;
  expectedVersion: number;
  reason: string;
}
export interface ApplicationSettingFormProperty {
  id: string;
  name: string;
  enabled: boolean;
  version: number;
}

export function ApplicationSettingForm({
  property,
  onSave,
  canSubmit = true,
}: {
  property: ApplicationSettingFormProperty;
  onSave: (draft: ApplicationSettingDraft) => Promise<ApplicationSettingResult>;
  canSubmit?: boolean;
}) {
  const [enabled, setEnabled] = useState(property.enabled);
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState<string>();
  const [saveError, setSaveError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dirty = enabled !== property.enabled || reason.length > 0;

  useEffect(() => {
    if (canSubmit) {
      setSaveError((current) => (current === refreshFailureMessage ? undefined : current));
    }
  }, [canSubmit]);

  const review = () => {
    const normalizedReason = reason.trim();
    if (!normalizedReason) {
      setValidationError('변경 사유를 입력하세요.');
      return;
    }
    if (reason.length > MAX_REASON_LENGTH) {
      setValidationError('사유는 240자 이내로 입력하세요.');
      return;
    }
    setValidationError(undefined);
    setConfirmOpen(true);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      const result = await onSave({
        enabled,
        expectedVersion: property.version,
        reason: reason.trim(),
      });
      setConfirmOpen(false);
      setEnabled(result.enabled);
      setReason('');
      toast.success('신청 허용 설정을 저장했습니다.');
    } catch (error) {
      setConfirmOpen(false);
      setSaveError(
        error instanceof ApplicationSettingRefreshError
          ? refreshFailureMessage
          : isApiError(error) && error.status === 409
            ? '지점 설정이 먼저 변경되었습니다. 최신 설정을 확인한 뒤 다시 결정하세요.'
            : isApiError(error) && (error.kind === 'network' || error.kind === 'timeout')
              ? '요청 결과를 확인하지 못했습니다. 현재값을 확인하세요. 요청을 자동으로 다시 보내지 않았습니다.'
              : '저장하지 못했습니다. 입력한 값을 보존했습니다.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPage.Section title="신청 허용 설정" className="bg-card rounded-lg border p-4 sm:p-5">
      <div className="flex flex-col gap-4">
        <UnsavedGuard when={dirty} />
        <div>
          <p className="label text-foreground">{property.name}</p>
          <p className="body-sm text-muted-foreground">
            대상 지점 · 변경 버전 {String(property.version)}
          </p>
        </div>
        <div className="border-border bg-muted/30 flex items-center justify-between gap-4 rounded-md border p-3 sm:p-4">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="label text-foreground">숙박 신청 허용</span>
            <span className="caption text-muted-foreground">신청 허용만 변경합니다.</span>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(value) => {
              setEnabled(value);
              setSaveError(undefined);
            }}
            aria-label="숙박 신청 허용"
          />
        </div>
        <p className="body-sm text-muted-foreground">
          신청 허용은 판매 가능 여부나 재고를 바꾸지 않습니다.
        </p>
        <FormField
          label="변경 사유"
          htmlFor="application-setting-reason"
          error={validationError !== undefined}
          message={validationError ?? `${String(reason.length)} / ${String(MAX_REASON_LENGTH)}자`}
        >
          <Textarea
            id="application-setting-reason"
            rows={3}
            value={reason}
            aria-invalid={validationError !== undefined}
            maxLength={MAX_REASON_LENGTH + 1}
            onChange={(event) => {
              setReason(event.target.value);
              setValidationError(undefined);
              setSaveError(undefined);
            }}
          />
        </FormField>
        {saveError && (
          <Alert variant="warning">
            <AlertDescription>{saveError}</AlertDescription>
          </Alert>
        )}
        {dirty && <p className="body-sm text-muted-foreground">저장하지 않은 변경이 있습니다.</p>}
        {!canSubmit && (
          <p className="body-sm text-muted-foreground">
            최신 지점 설정을 확인할 수 없어 다시 조회할 때까지 저장을 막았습니다.
          </p>
        )}
        <Button
          type="button"
          tone="brand"
          className="min-h-11 w-full sm:w-fit"
          disabled={!dirty || saving || !canSubmit}
          onClick={review}
        >
          변경 검토
        </Button>
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          target={property.name}
          action="신청 허용 변경"
          consequence={`${property.enabled ? '허용' : '차단'} → ${enabled ? '허용' : '차단'} · 사유: ${reason.trim()}`}
          variant="default"
          onConfirm={() => void save()}
        />
      </div>
    </FormPage.Section>
  );
}
